import { useWorkspaceText } from './interface-copy';
import { useState, type ReactNode } from 'react';
import { WorkspaceIcon } from './icons';
import { dateLabel } from './primitives';
import { connectionStatusLabel } from './connection-status';
import {
  connectionAvailabilityMessage,
  connectionReadinessMessage,
} from './connection-availability';
import type { ScopedConnectionItem as CompanyConnectionItem } from './connection-api';
import { ConnectionAccessCard } from './connect-access/policy-card';
import type { ConnectionReadItem } from './connection-read-access/api';
import { connectionLogoUrl } from './connection-logo';
import { useIntlLocale } from './intl-locale';

function StatusMark({ status, busy }: { status: CompanyConnectionItem['status']; busy: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useIntlLocale();
  if (busy)
    return (
      <span className="ws-connector-mark ws-connector-mark-busy" aria-label={uiText('Waiting')} />
    );
  if (status === 'connected')
    return (
      <span className="ws-connector-mark ws-connector-mark-ok" aria-label={uiText('Connected')}>
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <circle cx="10" cy="10" r="10" fill="currentColor" />
          <path
            d="M6 10.5l2.6 2.5L14 7.5"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  if (status === 'needs_reauthorization' || status === 'unavailable')
    return (
      <span
        className="ws-connector-mark ws-connector-mark-warn"
        aria-label={uiText('Needs reconnecting')}
      >
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <path
            d="M10 2.5l8 14H2z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M10 8v4.2M10 15h.01"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  if (status === 'not_configured' || status === 'disabled')
    return (
      <span
        className="ws-connector-mark ws-connector-mark-locked"
        aria-label={connectionStatusLabel(status, locale)}
      >
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <rect
            x="4.5"
            y="9"
            width="11"
            height="8"
            rx="1.8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path d="M7 9V6.5a3 3 0 0 1 6 0V9" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </span>
    );
  return <span className="ws-connector-mark ws-connector-mark-connect">{uiText('Connect')}</span>;
}

export function ConnectorCard({
  item,
  busy,
  open,
  onActivate,
  onDisconnect,
  onReconnect,
  summary,
  access,
  accessSummary,
  policy,
  onReadSaved,
  onReadStale,
}: {
  item: CompanyConnectionItem;
  busy: boolean;
  open: boolean;
  onActivate: () => void;
  onDisconnect: (confirmed?: boolean) => void | Promise<void>;
  onReconnect: () => void;
  summary?: string;
  /** Reading for this connected account (connect-time access), drawn in the open panel. */
  access?: ReactNode;
  /** The one quiet line under a connected account: "Reads automatically · Changes ask". */
  accessSummary?: string;
  /** The admin's policy row for this connection (G11), drawn in the open panel. */
  policy?: ReactNode;
  onReadSaved?: (next: ConnectionReadItem) => void;
  onReadStale?: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useIntlLocale();
  const [logoFailed, setLogoFailed] = useState(false);
  if (item.readAccess)
    return (
      <ConnectionAccessCard
        item={item.readAccess}
        logoId={item.id}
        identity={item.accountLabel}
        busy={busy}
        onSaved={onReadSaved ?? (() => onReadStale?.())}
        onStale={onReadStale ?? (() => undefined)}
        onReconnect={
          item.actions.connect.available ? (item.appSetup ? onActivate : onReconnect) : undefined
        }
        onRemove={
          item.actions.disconnect.available
            ? async () => {
                await onDisconnect(true);
              }
            : undefined
        }
      />
    );
  const readinessMessage = connectionReadinessMessage(item.readiness, locale);
  const detail = readinessMessage
    ? readinessMessage
    : item.status === 'connected'
      ? [
          item.accountLabel || uiText('Account identity unavailable'),
          item.connectedAt ? dateLabel(item.connectedAt, uiText.locale) : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : item.status === 'needs_reauthorization'
        ? uiText('Access expired or was revoked. Reconnect to continue.')
        : item.status === 'unavailable'
          ? uiText('Account authorized · tools unavailable')
          : item.status === 'not_configured'
            ? uiText('Not available in this workspace yet.')
            : (summary ?? item.description);
  return (
    <article className={`ws-connector-card${open ? ' is-open' : ''}`} data-status={item.status}>
      <button
        type="button"
        className="ws-connector-main"
        onClick={onActivate}
        disabled={busy}
        aria-expanded={open}
        aria-label={`${item.name}: ${connectionStatusLabel(item.status, locale)} · ${item.audience === 'private' ? uiText('Only me') : uiText('Everyone in the company')}`}
      >
        <span className="ws-connector-logo" aria-hidden="true">
          {logoFailed ? (
            <WorkspaceIcon name="connections" size={26} />
          ) : (
            <img src={connectionLogoUrl(item.id)} alt="" onError={() => setLogoFailed(true)} />
          )}
        </span>
        <span className="ws-connector-text">
          <span className="ws-connector-name">{item.name}</span>
          <span className="ws-connector-detail">{detail}</span>
          {accessSummary && <span className="ws-connector-access">{accessSummary}</span>}
        </span>
        <StatusMark status={item.status} busy={busy} />
      </button>
      {open && (
        <div className="ws-connector-panel">
          <p>{item.description}</p>
          <p>
            {uiRich('{value1}{value2} The provider’s account permissions still apply.', {
              value1:
                item.audience === 'private'
                  ? uiText('Audience: only you.')
                  : uiText('Audience: everyone in the company.'),
              value2: ' ',
            })}
          </p>
          {item.status === 'needs_reauthorization' && item.lastError && !readinessMessage && (
            <p className="ws-connector-error">
              {uiRich('Last error: {value1}', { value1: item.lastError })}
            </p>
          )}
          {item.status === 'unavailable' && !readinessMessage && (
            <p className="ws-connector-error">
              {connectionAvailabilityMessage(item.lastError ?? undefined, locale)}
            </p>
          )}
          {readinessMessage && item.status !== 'connected' && (
            <p className="ws-connector-error">{readinessMessage}</p>
          )}
          {item.readiness?.state === 'ready' && (
            <p className="ws-connector-detail">
              {item.readiness.toolCount !== undefined
                ? uiText('{value1} {value2} ready.', {
                    value1: item.readiness.toolCount,
                    value2:
                      item.readiness.toolCount === 1 ? uiText('tool is') : uiText('tools are'),
                  })
                : null}
              {item.readiness.accountCount !== undefined
                ? ' ' +
                  uiText('{value1} ad {value2} available.', {
                    value1: item.readiness.accountCount,
                    value2:
                      item.readiness.accountCount === 1
                        ? uiText('account is')
                        : uiText('accounts are'),
                  })
                : null}
            </p>
          )}
          {access}
          <div className="ws-connector-actions">
            {item.actions.connect.available && (
              <button className="ws-text-button" disabled={busy} onClick={onReconnect}>
                {item.appSetup
                  ? uiText('Continue with my Meta app')
                  : item.status === 'needs_reauthorization'
                    ? uiText('Reconnect')
                    : uiText('Connect')}
              </button>
            )}
            {item.actions.disconnect.available && (
              <button
                className="ws-text-button ws-text-button-quiet"
                disabled={busy}
                onClick={() => void onDisconnect()}
              >
                {uiText('Disconnect')}
              </button>
            )}
            {!item.actions.connect.available && !item.actions.disconnect.available && (
              <span className="ws-connector-detail">
                {connectionStatusLabel(item.status, locale)}
              </span>
            )}
          </div>
          {policy}
        </div>
      )}
    </article>
  );
}
